"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { ButtonLink } from "@/components/ui/button-link";
import { CheckCircle } from "lucide-react";

interface HeroSplitProps {
  title: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  features?: string[];
  benefits?: string[];
  image?: string;
}

const HeroSplit = ({ title, subtitle, ctaText, ctaHref, features, benefits, image }: HeroSplitProps) => {
  // Support both "features" (string[]) and "benefits" (string[]) prop names
  const featureList = features || benefits;
  return (
    <section className="py-24 md:py-32 px-6 bg-white">
      <div className="container max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left Column - Text */}
          <div className="space-y-6">
            <BlurFade delay={0.1}>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.1]">
                {title}
              </h1>
            </BlurFade>

            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-lg text-foreground/60 leading-relaxed">
                  {subtitle}
                </p>
              </BlurFade>
            )}

            {featureList && featureList.length > 0 && (
              <BlurFade delay={0.2}>
                <ul className="space-y-3">
                  {featureList.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-foreground/70">
                      <CheckCircle size={18} className="text-primary flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </BlurFade>
            )}

            {ctaText && ctaHref && (
              <BlurFade delay={0.25}>
                <div className="pt-2">
                  <ButtonLink href={ctaHref} variant="primary" size="md">
                    {ctaText}
                  </ButtonLink>
                </div>
              </BlurFade>
            )}
          </div>

          {/* Right Column - Visual */}
          <BlurFade delay={0.2}>
            <div className="relative">
              {image ? (
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src={image}
                    alt={title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] rounded-3xl bg-secondary flex items-center justify-center">
                  <div className="text-center space-y-4 p-8">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                      <div className="w-8 h-8 rounded-lg bg-primary/20" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 w-32 bg-foreground/10 rounded-full mx-auto" />
                      <div className="h-3 w-24 bg-foreground/10 rounded-full mx-auto" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </BlurFade>
        </div>
      </div>
    </section>
  );
};

export default HeroSplit;
